import { ProductEvent, type ProductEventName } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';
import logger from '@widgets/logger';
import { NIL as EMPTY_GUID } from 'uuid';

type EventProperties = Record<string, unknown>;
type PropertiesSource<T> = EventProperties | ((response: T) => EventProperties);

export function captureWhen<T>(
    shouldCapture: (response: T) => boolean,
    event: ProductEventName,
    properties?: PropertiesSource<T>,
) {
    return (response: T): T => {
        try {
            if (shouldCapture(response)) {
                PostHogManager.capture(event, typeof properties === 'function' ? properties(response) : properties);
            }
        } catch (error) {
            logger.error(`Product event "${event}" was not captured:`, error);
        }
        return response;
    };
}

export function captureOnSuccess<T>(event: ProductEventName, properties?: PropertiesSource<T>) {
    return captureWhen<T>(() => true, event, properties);
}

export function captureLocationsImported(properties: { source: string; count: number } & EventProperties) {
    if (properties.count > 0) {
        PostHogManager.capture(ProductEvent.LocationsImported, properties);
    }
}

export function captureOnCreate<T>(
    payload: { id?: unknown } | undefined,
    event: ProductEventName,
    properties?: PropertiesSource<T>,
) {
    return captureWhen<T>(() => !payload?.id || payload.id === EMPTY_GUID, event, properties);
}
